// §9.6 / §18 F6 — is an installed app's origin still there?
//
// One `HEAD <entry>` answers it, and §9.6 says when to ask: on install, on launch, and every ten
// minutes. Three *consecutive* misses mark the app unhealthy and dim its icon; the counting and the
// persistence belong to the registry (`apps/registry.ts`), so this service is only the schedule and
// the probe.
//
// **Nothing here ever uninstalls anything.** §9.6 is explicit, and the registry has a test that
// says so; this file has one too, because the schedule is where an "obviously it's gone" shortcut
// would be tempting to write.
import type { AppHealth } from './registry';

/** §9.6 — every ten minutes, for installed apps. */
export const HEALTH_INTERVAL_MS = 10 * 60 * 1000;

export interface HealthService {
  /**
   * Probe one app now and record the outcome. Resolves to whether the origin answered — which is
   * *not* the same as whether the app is healthy: one miss is not three.
   */
  check(appId: string): Promise<boolean>;
  /** Probe every installed app. What the ten-minute tick does. */
  checkAll(): Promise<void>;
  /** Start the §9.6 schedule: probe anything new, then tick every `intervalMs`. */
  start(): void;
  stop(): void;
  /** §9.6 — is this app currently dimmed? Reads the registry, so it needs no state of its own. */
  unhealthy(appId: string): boolean;
}

/** The slice of the registry this service touches. */
export interface HealthRegistry {
  list(): Array<{ id: string; manifest: { entry: string }; health: AppHealth }>;
  get(appId: string): { manifest: { entry: string }; health: AppHealth } | undefined;
  recordHealth(appId: string, healthy: boolean): Promise<AppHealth | null>;
}

export interface HealthDeps {
  installed: HealthRegistry;
  /** `HEAD <entry>` — true when the origin answered at all (D-116). Injected so tests are offline. */
  head: (url: string) => Promise<boolean>;
  /** Told after every probe, so the bar and the launcher can re-read the dim state. */
  onResult?: (appId: string, healthy: boolean) => void;
  /** Told when the installed set changes, so a newly installed app is probed once (§9.6). */
  subscribe?: (onChange: () => void) => () => void;
  intervalMs?: number;
  timers?: {
    setInterval: (fn: () => void, ms: number) => unknown;
    clearInterval: (handle: unknown) => void;
  };
}

/**
 * D-116 — the production probe. `mode: 'no-cors'` because §9.6's check asks *is the origin
 * reachable*, not *what did it say*: an external app's server has no reason to send us CORS
 * headers, and requiring them would report every correctly-configured app as down. An opaque
 * response is a reachable server; a network error is not.
 */
export async function headProbe(url: string): Promise<boolean> {
  try {
    await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'follow',
    });
    return true;
  } catch {
    return false;
  }
}

export function createHealthService(deps: HealthDeps): HealthService {
  const intervalMs = deps.intervalMs ?? HEALTH_INTERVAL_MS;
  const timers = deps.timers ?? {
    setInterval: (fn, ms) => setInterval(fn, ms),
    clearInterval: (handle) => clearInterval(handle as ReturnType<typeof setInterval>),
  };
  /** Apps probed at least once. An app leaves it when it is uninstalled, so a re-install re-probes. */
  const probed = new Set<string>();
  let handle: unknown = null;
  let unsubscribe: (() => void) | null = null;

  async function probe(appId: string, entry: string): Promise<boolean> {
    probed.add(appId);
    const healthy = await deps.head(entry);
    await deps.installed.recordHealth(appId, healthy);
    deps.onResult?.(appId, healthy);
    return healthy;
  }

  async function checkAll(): Promise<void> {
    for (const app of deps.installed.list()) await probe(app.id, app.manifest.entry);
  }

  /** Probe apps this service has never seen — an install, or the first tick after a reload. */
  async function probeNew(): Promise<void> {
    const live = new Set<string>();
    for (const app of deps.installed.list()) {
      live.add(app.id);
      if (!probed.has(app.id)) await probe(app.id, app.manifest.entry);
    }
    for (const id of [...probed]) if (!live.has(id)) probed.delete(id);
  }

  return {
    async check(appId) {
      const app = deps.installed.get(appId);
      if (!app) return false;
      return probe(appId, app.manifest.entry);
    },

    checkAll,

    start() {
      if (handle !== null) return;
      unsubscribe = deps.subscribe?.(() => void probeNew()) ?? null;
      void probeNew();
      handle = timers.setInterval(() => void checkAll(), intervalMs);
    },

    stop() {
      if (handle !== null) timers.clearInterval(handle);
      handle = null;
      unsubscribe?.();
      unsubscribe = null;
    },

    unhealthy: (appId) => deps.installed.get(appId)?.health === 'unhealthy',
  };
}
